// src/pages/tenant/TenantDangerZonePage.tsx
// Destructive tenant operations: suspend, migrate, delete.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { SuspendTenantDialog, toSuspensionReasonEnum, type SuspensionReason as SuspendDialogReason } from '@/features/tenant/SuspendTenantDialog';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format ISO date to localized string. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Human-readable suspension reason label. */
function reasonLabel(reason: string, t: (k: string) => string): string {
  switch (reason) {
    case 'BILLING_FAILURE':
      return t('tenant.danger.reasonBilling');
    case 'POLICY_VIOLATION':
      return t('tenant.danger.reasonPolicy');
    case 'SECURITY_INCIDENT':
      return t('tenant.danger.reasonSecurity');
    case 'ABUSE':
      return t('tenant.danger.reasonAbuse');
    case 'LEGAL':
      return t('tenant.danger.reasonLegal');
    case 'OWNER_REQUEST':
      return t('tenant.danger.reasonOwner');
    case 'INACTIVITY':
      return t('tenant.danger.reasonInactivity');
    case 'QUOTA_EXCEEDED':
      return t('tenant.danger.reasonQuota');
    default:
      return reason;
  }
}

/** Map suspension reason to badge variant. */
function reasonVariant(reason: string): 'error' | 'warning' | 'default' | 'secondary' {
  switch (reason) {
    case 'BILLING_FAILURE':
    case 'ABUSE':
    case 'SECURITY_INCIDENT':
      return 'error';
    case 'POLICY_VIOLATION':
    case 'LEGAL':
    case 'QUOTA_EXCEEDED':
      return 'warning';
    case 'OWNER_REQUEST':
    case 'INACTIVITY':
      return 'default';
    default:
      return 'secondary';
  }
}

/** Calculate duration between two dates as a human-readable string. */
function durationBetween(start: string | undefined, end: string | undefined): string {
  if (!start) return '--';
  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : Date.now();
  const diffMs = endTime - startTime;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const minutes = Math.floor(diffMs / (1000 * 60));
  return `${minutes}m`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantDangerZonePage - destructive operations for a tenant.
 * Route: /tenants/:id/settings/danger
 */
export const TenantDangerZonePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTenant();
  const crud = useTenantTenant();
  const { t } = useI18n();

  const [suspensionHistory, setSuspensionHistory] = useState<any[]>([]);
  const [pendingMigration, setPendingMigration] = useState<any | null>(null);

  const tenant = crud.currentItem;
  const tenantName = tenant?.name ?? tenant?.slug ?? '';
  const tenantSlug = tenant?.slug ?? '';
  // Active suspension = most recent history entry that was never reactivated.
  const suspension = suspensionHistory.find((s: any) => !s.reactivated_at);
  const isSuspended = !!suspension || !!tenant?.suspended_at;

  // --- Suspend dialog ---
  const [showSuspend, setShowSuspend] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // --- History collapse ---
  const [historyOpen, setHistoryOpen] = useState(false);

  // --- Migrate form ---
  const [targetPlatformId, setTargetPlatformId] = useState('');
  const [migrationReason, setMigrationReason] = useState('');
  const [migrateIncludeMembers, setMigrateIncludeMembers] = useState(true);
  const [migrateIncludeContent, setMigrateIncludeContent] = useState(true);
  const [migrateIncludeAuditLogs, setMigrateIncludeAuditLogs] = useState(false);
  const [migrateIncludeSubscriptions, setMigrateIncludeSubscriptions] = useState(true);
  const [migrateDryRun, setMigrateDryRun] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [cancellingMigration, setCancellingMigration] = useState(false);

  // --- Delete form ---
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadSuspensionHistory = async (tenantId: string) => {
    try {
      const res = await store.GetTenantSuspensionHistory(tenantId);
      const items = res?.suspensions?.items;
      setSuspensionHistory(Array.isArray(items) ? items : []);
    } catch {
      // Error handled by store
    }
  };

  useEffect(() => {
    if (id) {
      crud.GetTenant(id).catch(() => { /* Error handled by store */ });
      loadSuspensionHistory(id);
    }
  }, [id]);

  // --- Handlers ---

  const handleSuspend = async (reason: SuspendDialogReason, detail: string) => {
    if (!id) return;
    await store.SuspendTenant(id, {
      reason: toSuspensionReasonEnum(reason) as any,
      reason_detail: detail,
      data_accessible: true,
      reactivation_conditions: [],
    });
    await loadSuspensionHistory(id);
  };

  const handleReactivate = async () => {
    if (!id) return;
    setReactivating(true);
    try {
      await store.ReactivateTenant(id, {});
      await loadSuspensionHistory(id);
    } catch {
      // Error handled by store
    } finally {
      setReactivating(false);
    }
  };

  const handleRequestMigration = async () => {
    if (!id || !targetPlatformId.trim()) return;
    setMigrating(true);
    try {
      const res = await store.RequestTenantMigration(id, {
        target_platform: { id: targetPlatformId } as any,
        migration_reason: migrationReason,
        include_members: migrateIncludeMembers,
        include_content: migrateIncludeContent,
        include_audit_logs: migrateIncludeAuditLogs,
        include_subscriptions: migrateIncludeSubscriptions,
        dry_run: migrateDryRun,
      });
      setPendingMigration(res?.migration ?? null);
      setTargetPlatformId('');
      setMigrationReason('');
    } catch {
      // Error handled by store
    } finally {
      setMigrating(false);
    }
  };

  const handleCancelMigration = async () => {
    const migrationId = pendingMigration?.entity?.id ?? pendingMigration?.id;
    if (!id || !migrationId) return;
    setCancellingMigration(true);
    try {
      await store.CancelTenantMigration(id, migrationId, {});
      setPendingMigration(null);
    } catch {
      // Error handled by store
    } finally {
      setCancellingMigration(false);
    }
  };

  const handleDelete = async () => {
    if (!id || deleteConfirmInput !== tenantSlug) return;
    if (!confirm(t('tenant.danger.deleteDoubleConfirm'))) return;
    setDeleting(true);
    try {
      await crud.DeleteTenant(id);
      navigate('/tenants');
    } catch {
      // Error handled by store
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {tenantName}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}/settings`} className="text-muted-foreground hover:text-foreground">
          {t('tenant.settings')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.danger.title')}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('tenant.danger.title')}</h1>

      {/* Warning alert */}
      <Alert variant="warning">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
          <span className="text-sm font-medium">
            {t('tenant.danger.warningMessage')}
          </span>
        </div>
      </Alert>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {crud.error ? <ErrorAlert error={crud.error} dismissible onDismiss={() => crud.clearError()} /> : null}

      {/* ================================================================= */}
      {/* Suspend Tenant */}
      {/* ================================================================= */}
      <Card className="border-yellow-300 dark:border-yellow-700">
        <CardHeader>
          <CardTitle className="text-yellow-600 dark:text-yellow-400">
            {t('tenant.danger.suspendTitle')}
          </CardTitle>
          <CardDescription>
            {t('tenant.danger.suspendDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isSuspended ? (
            /* Currently suspended - show info + reactivate */
            <div className="space-y-4">
              <Alert variant="warning">
                {t('tenant.danger.currentlySuspended')}
              </Alert>

              <dl className="space-y-3">
                {suspension?.reason && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-muted-foreground">{t('tenant.danger.reasonLabel')}</dt>
                    <dd>
                      <Badge variant={reasonVariant(suspension.reason)} size="sm">
                        {reasonLabel(suspension.reason, t)}
                      </Badge>
                    </dd>
                  </div>
                )}
                {suspension?.reason_detail && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-muted-foreground">{t('tenant.danger.detailLabel')}</dt>
                    <dd className="text-sm text-foreground">{suspension.reason_detail}</dd>
                  </div>
                )}
                {suspension?.suspended_by && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-muted-foreground">{t('tenant.danger.suspendedByLabel')}</dt>
                    <dd className="text-sm text-foreground">
                      {suspension.suspended_by?.name ?? suspension.suspended_by?.id ?? '--'}
                    </dd>
                  </div>
                )}
                {(suspension?.suspended_at || tenant?.suspended_at) && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-muted-foreground">{t('tenant.danger.suspendedAtLabel')}</dt>
                    <dd className="text-sm text-foreground">
                      {formatDate(suspension?.suspended_at ?? tenant?.suspended_at)}
                    </dd>
                  </div>
                )}
                {suspension?.reactivation_conditions && suspension.reactivation_conditions.length > 0 && (
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">
                      {t('tenant.danger.reactivationConditions')}
                    </dt>
                    <dd>
                      <ul className="list-disc list-inside space-y-1">
                        {suspension.reactivation_conditions.map((cond: string, idx: number) => (
                          <li key={idx} className="text-sm text-foreground">{cond}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
              </dl>

              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleReactivate}
                disabled={reactivating}
              >
                {reactivating ? t('common.loading') : t('tenant.danger.reactivateButton')}
              </Button>
            </div>
          ) : (
            /* Not suspended - suspend via dialog (reason + detail) */
            <Button
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
              onClick={() => setShowSuspend(true)}
            >
              {t('tenant.danger.suspendButton')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Suspension History */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <button
            className="flex items-center gap-2 w-full text-left"
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            <svg
              className={`w-4 h-4 text-muted-foreground transition-transform ${historyOpen ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            <CardTitle className="text-base">
              {t('tenant.danger.historyTitle')}
            </CardTitle>
            <span className="text-sm text-muted-foreground ml-1">
              ({suspensionHistory.length})
            </span>
          </button>
        </CardHeader>
        {historyOpen && (
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('tenant.danger.historyReason')}</TableHead>
                <TableHead>{t('tenant.danger.historySuspendedBy')}</TableHead>
                <TableHead>{t('tenant.danger.historySuspendedAt')}</TableHead>
                <TableHead>{t('tenant.danger.historyDuration')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suspensionHistory.length === 0 ? (
                <TableEmpty colSpan={4} message={t('tenant.danger.noHistory')} />
              ) : (
                suspensionHistory.map((entry: any, idx: number) => (
                  <TableRow key={entry.id ?? idx}>
                    <TableCell>
                      <Badge variant={reasonVariant(entry.reason)} size="sm">
                        {reasonLabel(entry.reason, t)}
                      </Badge>
                      {entry.reason_detail && (
                        <p className="text-xs text-muted-foreground mt-1">{entry.reason_detail}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {entry.suspended_by?.name ?? entry.suspended_by?.id ?? '--'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(entry.suspended_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {durationBetween(entry.suspended_at, entry.reactivated_at)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* ================================================================= */}
      {/* Migrate Tenant */}
      {/* ================================================================= */}
      <Card className="border-orange-300 dark:border-orange-700">
        <CardHeader>
          <CardTitle className="text-orange-600 dark:text-orange-400">
            {t('tenant.danger.migrateTitle')}
          </CardTitle>
          <CardDescription>
            {t('tenant.danger.migrateDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingMigration ? (
            /* Pending migration - show status + cancel */
            <div className="space-y-4">
              <Alert variant="info">
                {t('tenant.danger.migrationInProgress')}
              </Alert>

              <dl className="space-y-3">
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-muted-foreground">{t('tenant.danger.migrationTarget')}</dt>
                  <dd className="text-sm font-medium text-foreground">
                    {pendingMigration.target_platform?.id ?? '--'}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-muted-foreground">{t('tenant.danger.migrationStatus')}</dt>
                  <dd>
                    <Badge variant="default" size="sm">
                      {pendingMigration.status ?? 'PENDING'}
                    </Badge>
                  </dd>
                </div>
                {pendingMigration.created_at && (
                  <div className="flex items-center justify-between">
                    <dt className="text-sm text-muted-foreground">{t('tenant.danger.migrationRequestedAt')}</dt>
                    <dd className="text-sm text-foreground">
                      {formatDate(pendingMigration.created_at)}
                    </dd>
                  </div>
                )}
              </dl>

              <Button
                variant="danger"
                onClick={handleCancelMigration}
                disabled={cancellingMigration}
              >
                {cancellingMigration ? t('common.loading') : t('tenant.danger.cancelMigration')}
              </Button>
            </div>
          ) : (
            /* No pending migration - show form */
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  {t('tenant.danger.targetPlatform')}
                </label>
                <Input
                  placeholder={t('tenant.danger.targetPlatformPlaceholder')}
                  value={targetPlatformId}
                  onChange={(e) => setTargetPlatformId(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  {t('tenant.danger.migrationReasonLabel')}
                </label>
                <textarea
                  value={migrationReason}
                  onChange={(e) => setMigrationReason(e.target.value)}
                  placeholder={t('tenant.danger.migrationReasonPlaceholder')}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t('tenant.danger.migrationOptions')}
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrateIncludeMembers}
                    onChange={(e) => setMigrateIncludeMembers(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('tenant.danger.includeMembers')}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrateIncludeContent}
                    onChange={(e) => setMigrateIncludeContent(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('tenant.danger.includeContent')}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrateIncludeAuditLogs}
                    onChange={(e) => setMigrateIncludeAuditLogs(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('tenant.danger.includeAuditLogs')}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrateIncludeSubscriptions}
                    onChange={(e) => setMigrateIncludeSubscriptions(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('tenant.danger.includeSubscriptions')}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={migrateDryRun}
                    onChange={(e) => setMigrateDryRun(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('tenant.danger.dryRun')}</span>
                </label>
              </div>

              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleRequestMigration}
                disabled={migrating || !targetPlatformId.trim()}
              >
                {migrating ? t('common.loading') : t('tenant.danger.migrateButton')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Delete Tenant */}
      {/* ================================================================= */}
      <Card className="border-red-300 dark:border-red-700">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400">
            {t('tenant.danger.deleteTitle')}
          </CardTitle>
          <CardDescription>
            {t('tenant.danger.deleteDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="error">
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('tenant.danger.deleteWarning')}</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('tenant.danger.deleteConsequence1')}</li>
                <li>{t('tenant.danger.deleteConsequence2')}</li>
                <li>{t('tenant.danger.deleteConsequence3')}</li>
                <li>{t('tenant.danger.deleteConsequence4')}</li>
              </ul>
            </div>
          </Alert>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('tenant.danger.deleteConfirmLabel', { slug: tenantSlug })}
            </label>
            <Input
              placeholder={tenantSlug}
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleting || deleteConfirmInput !== tenantSlug || !tenantSlug}
          >
            {deleting ? t('common.loading') : t('tenant.danger.deleteButton')}
          </Button>
        </CardFooter>
      </Card>

      <SuspendTenantDialog
        open={showSuspend}
        tenantName={tenantName}
        onClose={() => setShowSuspend(false)}
        onSubmit={handleSuspend}
      />
    </div>
  );
});

export default TenantDangerZonePage;
